/**
 * Commesse che hanno ancora qualcosa da mandare in fatturazione.
 *
 * È la lista di inizio mese: quanto resta da decidere su ogni commessa, da
 * quanto non si fattura, e il pulsante per mandarne una parte — o tutto — nel
 * mese che si sceglie. Senza, bisognava entrare in ogni commessa per scoprire
 * cosa era rimasto fuori.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fatturazionIApi } from '../api';
import MandaInFatturazione from './MandaInFatturazione';
import BarraFatturazione from './BarraFatturazione';

const fmt = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmtEur = n => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
const mesiDa = (d) => {
  if (!d) return null;
  return Math.round((new Date(new Date().toDateString()) - new Date(d)) / 86400000 / 30);
};

export default function DaMandare({ onCambiato, clienteId }) {
  const [dati, setDati] = useState(null);

  const carica = () => fatturazionIApi.daMandareTutte().then(setDati).catch(() => setDati({ righe: [], totale: 0 }));
  useEffect(() => { carica(); }, []);

  if (!dati) return <div className="text-sm text-gray-400 py-8 text-center">Caricamento…</div>;

  // Il filtro cliente della pagina vale anche qui: era l'unica vista che lo
  // ignorava, e sembrava che la ricerca non funzionasse.
  const righe = clienteId
    ? dati.righe.filter(r => String(r.cliente_id) === String(clienteId))
    : dati.righe;
  const totale = clienteId
    ? +righe.reduce((s, x) => s + Number(x.da_decidere || 0), 0).toFixed(2)
    : dati.totale;

  if (!righe.length) {
    return (
      <div className="card text-center py-10 text-gray-400">
        {clienteId
          ? 'Questo cliente non ha commesse con residuo da mandare in fatturazione.'
          : 'Nessuna commessa con residuo: tutto è già stato mandato in fatturazione.'}
      </div>
    );
  }

  const somma = (campo) => +righe.reduce((t, r) => t + Number(r[campo] || 0), 0).toFixed(2);
  const aggiornato = () => { carica(); onCambiato?.(); };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Ancora da mandare in fatturazione</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{fmt(totale)}</div>
          <div className="text-xs text-gray-400 mt-0.5 mb-2">
            su {righe.length} {righe.length === 1 ? 'commessa' : 'commesse'}: è il residuo del contratto, non ancora deciso
          </div>
          {/* Quanto vale, in percentuale, quello che resta: il totale da solo
              non dice se è tanto o poco. */}
          <BarraFatturazione alta
            budget={somma('budget')} fatturato={somma('fatturato')} inCoda={somma('in_coda')} />
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Come funziona</div>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
            Per il lavoro a corpo non serve un SAL: scegli quanto fatturare — tutto, una
            percentuale o un importo — e il mese di competenza. Nascono le fatture da emettere
            e il residuo scala da solo. Per il lavoro a giornate conviene invece approvare il
            rendiconto, che divide da sé consulenza e spese.
          </p>
        </div>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 text-left">
              <th className="px-4 py-3">Commessa</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3 text-right">Valore</th>
              <th className="px-4 py-3 text-right">Fatturato</th>
              <th className="px-4 py-3 text-right">In coda</th>
              <th className="px-4 py-3 text-right">Da decidere</th>
              <th className="px-4 py-3 w-44">Avanzamento</th>
              <th className="px-4 py-3">Ultima fattura</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {righe.map(r => {
              const mesi = mesiDa(r.ultima_fattura);
              return (
                <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <Link to={`/commesse/${r.id}`} className="font-medium text-brand-700 hover:underline">
                      {r.numero}
                    </Link>
                    <div className="text-xs text-gray-500 truncate max-w-[220px]">{r.titolo}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{r.cliente || '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{fmt(r.budget)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{fmt(r.fatturato)}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{r.in_coda ? fmt(r.in_coda) : '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-amber-700">{fmtEur(r.da_decidere)}</td>
                  <td className="px-4 py-3">
                    <BarraFatturazione budget={r.budget} fatturato={r.fatturato} inCoda={r.in_coda}
                                       mostraMancante={false} />
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {fmtDate(r.ultima_fattura)}
                    {mesi >= 4 && <div className="text-amber-700">da {mesi} mesi</div>}
                    {!r.ultima_fattura && <div className="text-amber-700">mai fatturata</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <MandaInFatturazione commessaId={r.id} titolo={r.titolo} onCambiato={aggiornato} compatto />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
